// src/components/ContactUs.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import SEOHelmet from './SEOHelmet';
import { submitChatLead } from '../services/chatLeadService';
import './ContactUs.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

const CONTACT_VIDEO = SITE_CONFIG.LANDING_PAGE.VIDEOS.SHOWCASE;

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    serviceReq: 'buying'
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isHoneypotSubmission(event.currentTarget)) {
      setSuccess(true);
      setFormData({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying' });
      setTimeout(() => setSuccess(false), 5000);
      return;
    }

    setSubmitting(true);
    setSuccess(false);
    setSubmitError('');

    try {
      const params = new URLSearchParams({
        postalCode: formData.postalCode,
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        serviceReq: formData.serviceReq,
        email: formData.email,
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await submitChatLead(params, { rememberContact: true });

      if (!response.ok) {
        throw new Error(`Contact request failed: ${response.status}`);
      }

      setSuccess(true);
      setFormData({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('We could not send your request just now. Please call or email Peter directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-us-page">
      <SEOHelmet
        title="Contact Peter Mergenthaler Jr | Integritos Real Estate"
        description="Contact Peter Mergenthaler Jr for buying, selling, investing, or relocation guidance across Palm Beach County and South Florida."
        canonical="https://integritosrealestate.com/contact-us"
      />
      <header className="contact-topbar">
        <button className="contact-home-button" onClick={() => navigate('/')} type="button">
          <span aria-hidden="true">&#8592;</span>
          Home
        </button>
        <div className="contact-brand">
          <span className="contact-brand-name">{SITE_CONFIG.AGENT.NAME}</span>
          <span className="contact-brand-divider" aria-hidden="true"></span>
          <span>South Florida Real Estate</span>
        </div>
      </header>

      <main className="contact-us-container">
        <section className="contact-welcome-card">
          <div className="contact-video-panel">
            <video
              className="contact-video"
              src={CONTACT_VIDEO}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
            />
            <div className="contact-video-shade"></div>
            <div className="contact-welcome-copy">
              <span className="contact-eyebrow">Let&apos;s start a conversation</span>
              <h1>Your next move should feel exciting.</h1>
              <p>
                Whether you are buying, selling, or simply exploring what is possible,
                Peter is here with local insight and personal guidance.
              </p>
              <div className="contact-promise-list" aria-label="What you can expect">
                <span>Local expertise</span>
                <span>Clear answers</span>
                <span>No-pressure guidance</span>
              </div>
            </div>
          </div>

          <div className="contact-form-panel">
            <div className="contact-form-heading">
              <span className="contact-form-kicker">How can I help?</span>
              <h2>Tell me about your plans.</h2>
              <p>Share a few details and I&apos;ll personally follow up with you.</p>
            </div>

            {success && (
              <div className="contact-status contact-status-success" role="status">
                <span aria-hidden="true">&#10003;</span>
                <div>
                  <strong>Thank you for reaching out.</strong>
                  <p>Peter will be in touch soon.</p>
                </div>
              </div>
            )}

            {submitError && (
              <div className="contact-status contact-status-error" role="alert">
                {submitError}
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
          <HoneypotField />
              <div className="contact-field contact-field-wide">
                <label htmlFor="contact-name">Full name</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="contact-field">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="contact-field">
                <label htmlFor="contact-phone">Phone</label>
                <input
                  id="contact-phone"
                  name="phoneNumber"
                  type="tel"
                  placeholder="(561) 555-0123"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  autoComplete="tel"
                  required
                />
              </div>

              <div className="contact-field">
                <label htmlFor="contact-zip">ZIP code</label>
                <input
                  id="contact-zip"
                  name="postalCode"
                  type="text"
                  inputMode="numeric"
                  placeholder="Your ZIP code"
                  value={formData.postalCode}
                  onChange={handleChange}
                  autoComplete="postal-code"
                  required
                />
              </div>

              <div className="contact-field">
                <label htmlFor="contact-service">I&apos;m interested in</label>
                <select
                  id="contact-service"
                  name="serviceReq"
                  value={formData.serviceReq}
                  onChange={handleChange}
                  required
                >
                  <option value="buying">Buying a home</option>
                  <option value="selling">Selling a home</option>
                  <option value="both">Buying and selling</option>
                </select>
              </div>

              <button className="contact-submit-button contact-field-wide" type="submit" disabled={submitting}>
                {submitting ? 'Sending your request...' : 'Start the conversation'}
                {!submitting && <span aria-hidden="true">&#8594;</span>}
              </button>
            </form>

            <div className="contact-direct">
              <p>Prefer to connect directly?</p>
              <div className="contact-direct-links">
                <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>
                  <span aria-hidden="true">Call</span>
                  {SITE_CONFIG.AGENT.PHONE}
                </a>
                <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>
                  <span aria-hidden="true">Email</span>
                  {SITE_CONFIG.AGENT.EMAIL}
                </a>
              </div>
            </div>
          </div>
        </section>

        <p className="contact-footer-note">
          Proudly helping buyers and sellers throughout South Florida.
        </p>
      </main>
    </div>
  );
};

export default ContactUs;
